import * as React from 'react';
import autobind from 'autobind-decorator';
import { computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import * as uuid from 'uuid';

import { CardController } from 'src/app/shared/card.controller';
import { DataLocalService } from 'src/core/services/data-local.service'
import { ContractStructureView } from './contract-structure.view';
import { CurrencyDefaultRateListDataService } from 'src/app/products/currency-default-rates/list/currency-default-rate-list.data';

interface ContractStructureControllerProps extends ComponentViewProps<StructureItem> {
	mode: ControlMode;
	onModeChanged: (mode: ControlMode) => void;
	type: 'cost' | 'price';
	contract: ContractItem;
	supplier: SupplierSummary;
	currency: CurrencySummary;
	currencies: CurrencySummary[];
	currencyChange: (newVal: any) => void;
	generatePrices: (priceCurrency: CurrencySummary) => void;
	onMarketChange?: (market: number) => void;
	selectedMarket?: number;
	productType: number;
}

@observer
export class ContractStructureController extends React.Component<ContractStructureControllerProps, any> {
	protected currencyDefaultRateDataService: CurrencyDefaultRateListDataService;

	@observable
	occupancyDataSet: DataSet<any>;

	@computed
	private get ageBands(): string[] {
		return this.props.contract == null ? [] : this.props.contract.dynamicProperties['ageBands'].map((e: AgeBandItem) => e.label ? e.label : e.type.toString());
	}

	constructor(props) {
		super(props);
		this.currencyDefaultRateDataService = CurrencyDefaultRateListDataService.make();
		this.currencyDefaultRateDataService.addSorting('id', 'asc', true);
		this.currencyDefaultRateDataService.load(query => query);
		this.occupancyDataSet = this.dataSetCreator(this.props.model.customOccupancyConfigs);
	}

	public componentWillReceiveProps(nextProps: Readonly<ContractStructureControllerProps>) {
		this.occupancyDataSet = this.dataSetCreator(nextProps.model.customOccupancyConfigs);
	}

	@autobind
	private dataSetCreator(items: CustomOccupancyPax[]): DataSet<CustomOccupancyPax> {
		if (items == null) {
			items = [];
		}

		//this.props.model.customOccupancyConfigs = items.slice(); // to force to be an "observable array" instead a proxy
		return new DataLocalService(this.props.model.customOccupancyConfigs);
	}

	@autobind
	refreshExchangeRates(fromCurrencyId: number, toCurrencyId: number) {
		const rate = this.getExchangeRate(fromCurrencyId, toCurrencyId);
		(this.props.model as PriceStructureItem).currencyExchangeRate = rate;
	}

	private getExchangeRate(fromCurrencyId: number, toCurrencyId: number) {
		const exchangeRate = this.currencyDefaultRateDataService.items.find(e => e.fromCurrencyId == fromCurrencyId && e.toCurrencyId == toCurrencyId);
		if (exchangeRate != null) {
			return exchangeRate.rate;
		} else {
			const defaultCurrency = this.props.currencies.find(e => e.isDefault);
			return this.getExchangeRate(fromCurrencyId, defaultCurrency.id) * this.getExchangeRate(defaultCurrency.id, toCurrencyId);
		}
	}

	render() {
		const model = this.props.model;
		let titlestr = (this.props.type == 'cost') ? 'Cost' : 'Price';
		return model &&
			<CardController icon='setting' title={titlestr + " Structure"} mode={this.props.mode} className='card-borderless'>
				<ContractStructureView
					productType={this.props.productType}
					mode={this.props.mode}
					model={model}
					type={this.props.type}
					contract={this.props.contract}
					currencies={this.props.currencies}
					currency={this.props.currency}
					currencyChange={this.props.currencyChange}
					setBusy={this.props.setBusy}
					generatePrices={this.props.generatePrices}
					selectedMarket={this.props.selectedMarket}
					onMarketChange={this.props.onMarketChange}
					supplier={this.props.supplier}
					refreshExchangeRates={this.refreshExchangeRates}
					occupancyDataSet={this.occupancyDataSet}
					ageBands={this.ageBands}
					getAllOccupancies={this.getAllOccupancies}
					newOccupancy={this.newOccupancy}
				/>
			</CardController>
	}

	@autobind
	protected createOccupancy() {
		const occupancy: CustomOccupancyPax = {
			id: uuid.v4()
		};
		for (let name of this.ageBands) {
			occupancy[name] = '0';
		}
		return occupancy;
	}

	@autobind
	protected createOccupancyByPossibility(possibilities: number[]) {
		const occupancy: CustomOccupancyPax = {
			id: uuid.v4()
		};
		for (let name = 0; name < this.ageBands.length; name++) {
			occupancy[this.ageBands[name].toLocaleLowerCase()] = possibilities[name].toString();
		}
		return occupancy;
	}

	@autobind
	protected newOccupancy() {
		const occupancy = this.createOccupancy();
		return this.occupancyDataSet.create(() => occupancy);
	}

	@autobind
	getAllOccupancies(maxPax: number) {
		this.occupancyDataSet = this.dataSetCreator([]);
		let possibilities = [];
		for (let i = 0; i <= maxPax; i++)
			possibilities.push(i);
		let array = [];
		for (let i = 0; i < this.ageBands.length; i++)
			array.push(possibilities);
		let result = [];
		if (array.length == 1) {
			for (let i = 0; i < array[0].length; i++) {
				let newArr = [array[0][i]];
				result.push(newArr);
			}
		} else {
			result = this.allPossibleCases(array, maxPax);
		}
		result = result.filter(x => x.reduce((a, b) => a + b, 0) > 0);
		for (let res of result) {
			const occupancy = this.createOccupancyByPossibility(res);
			this.occupancyDataSet.create(() => occupancy)
		}
	}

	allPossibleCases(arr: any[], target: number) {
		if (arr.length === 0) {
			return [];
		} else if (arr.length === 1) {
			return arr[0];
		} else {
			const result = [];
			const allCasesOfRest = this.allPossibleCases(arr.slice(1), target);  // recur with the rest of array
			for (let c of allCasesOfRest) {
				for (let i = 0; i < arr[0].length; i++) {
					let newArr = [arr[0][i]];
					newArr = newArr.concat(c);
					let sum = newArr.reduce((a, b) => a + b, 0);
					if (sum <= target)
						result.push(newArr);
				}
			}
			return result;
		}
	}
}



// WEBPACK FOOTER //
// ./src/app/products/contract/form/parts/contract-structure.controller.tsx